import type { AutomationCaseRefDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Code2, FileCode2, MessageSquarePlus, Play, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Textarea,
  Th,
} from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import {
  useAutomationCan,
  useClasses,
  useClassNotes,
  useSetClassNote,
  useTestPlan,
} from '../hooks';
import { useProjectCaseRefs } from '../case-links/api';
import { useFailToast } from './shared';

/** A note lives apart from the class catalogue, so a new scan leaves it alone. */
const ClassNoteDialog = ({
  projectId,
  className,
  initial,
  onClose,
}: {
  projectId: string;
  className: string;
  initial: string;
  onClose: () => void;
}) => {
  const { t } = useT();
  const failToast = useFailToast();
  const save = useSetClassNote(projectId);
  const [text, setText] = useState(initial);

  const submit = () =>
    save.mutate(
      { className, note: text },
      {
        onSuccess: () => {
          toast.success(
            text.trim()
              ? t('automation.project.classes.noteSaved')
              : t('automation.project.classes.noteRemoved'),
          );
          onClose();
        },
        onError: failToast,
      },
    );

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={t('automation.project.classes.noteTitle', { name: className })}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={text === initial}
            loading={save.isPending}
            onClick={submit}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field
        label={t('automation.project.classes.noteText')}
        hint={t('automation.project.classes.noteHint')}
      >
        {({ id, describedBy }) => (
          <Textarea
            id={id}
            rows={3}
            autoFocus
            aria-describedby={describedBy}
            value={text}
            placeholder={t('automation.project.classes.notePlaceholder')}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
};

/** The test cases a class or its tests cover — linked in TMS or by @TmsLink in the code. */
const CaseKeys = ({ refs }: { refs: AutomationCaseRefDto[] }) => {
  const { t } = useT();
  if (refs.length === 0) return null;
  return (
    <span
      className="mt-1 flex flex-wrap items-center gap-1"
      aria-label={t('automation.caseLinks.casesOf')}
    >
      {refs.map((ref) => (
        <Link
          key={`${ref.caseKey}-${ref.testMethod ?? ''}`}
          to="/projects/$projectCode/test-cases/$caseKey"
          params={{ projectCode: ref.projectCode, caseKey: ref.caseKey }}
          title={ref.testMethod ? `${ref.title} — ${ref.testMethod}` : ref.title}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-1.5 py-0.5 font-mono text-[11.5px] text-accent-fg hover:underline"
        >
          {ref.source === 'code' ? <Code2 className="size-3" aria-hidden="true" /> : null}
          {ref.caseKey}
        </Link>
      ))}
    </span>
  );
};

/**
 * Test classes: filter by tag, pick several to run, a note per class (QA). A
 * class is named the way Allure names it — its @DisplayName from the scanned
 * plan, the testdata comment as a fallback and a tooltip.
 */
export const ClassesTab = ({ projectId }: { projectId: string }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const can = useAutomationCan();
  const classes = useClasses(projectId);
  const notes = useClassNotes(projectId);
  const plan = useTestPlan(projectId);
  const caseRefs = useProjectCaseRefs(projectId);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [tagFilter, setTagFilter] = useState('');

  const titles = useMemo(() => {
    const byClass = new Map<string, { short: string; full?: string }>();
    for (const info of plan.data?.classes ?? []) {
      const short = info.displayName ?? info.comment;
      if (short)
        byClass.set(info.class, { short, ...(info.comment ? { full: info.comment } : {}) });
    }
    return byClass;
  }, [plan.data]);

  const allTags = useMemo(
    () => [...new Set((classes.data ?? []).flatMap((c) => c.tags))],
    [classes.data],
  );
  const shown = useMemo(
    () => (classes.data ?? []).filter((c) => !tagFilter || c.tags.includes(tagFilter)),
    [classes.data, tagFilter],
  );

  if (classes.error && !classes.data) {
    return (
      <Card>
        <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />
      </Card>
    );
  }
  if (!classes.data) return <SkeletonRows rows={5} className="p-0" />;

  if (classes.data.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileCode2 className="size-6" />}
          title={t('automation.project.classes.empty')}
          description={t('automation.project.classes.emptyHint')}
        />
      </Card>
    );
  }

  const toggle = (name: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const allSelected = shown.length > 0 && shown.every((c) => selected.has(c.name));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of shown) {
        if (allSelected) next.delete(c.name);
        else next.add(c.name);
      }
      return next;
    });

  // the run form opens with these classes chosen
  const startRun = (classes: string[]) =>
    void navigate({ to: '/automation/runs/new', search: { project: projectId, class: classes } });

  const chip = (active: boolean) =>
    cn(
      'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[12px] font-medium transition-colors',
      active
        ? 'border-accent bg-accent-soft text-accent-fg'
        : 'border-border bg-surface text-ink-muted hover:text-ink',
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="group"
          aria-label={t('automation.project.classes.tagFilter')}
          className="flex flex-wrap gap-1.5"
        >
          <button
            type="button"
            aria-pressed={tagFilter === ''}
            className={chip(tagFilter === '')}
            onClick={() => setTagFilter('')}
          >
            {t('automation.project.classes.all')}
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              aria-pressed={tagFilter === tag}
              className={chip(tagFilter === tag)}
              onClick={() => setTagFilter(tag)}
            >
              <Tag className="size-3" aria-hidden="true" />
              {tag}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-ink-subtle">
            {t('automation.project.classes.selected', { count: selected.size })}
          </span>
          {can.run ? (
            <Button
              variant="primary"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => startRun([...selected])}
            >
              <Play className="size-3.5" />
              {t('automation.project.classes.runSelected', { count: selected.size })}
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th className="w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label={t('automation.project.classes.selectAll')}
                    className="size-4 rounded border-border"
                  />
                </Th>
                <Th>{t('automation.project.classes.scenario')}</Th>
                <Th>{t('automation.project.classes.suite')}</Th>
                <Th>{t('automation.project.classes.tests')}</Th>
                <Th>{t('automation.project.classes.tags')}</Th>
                {can.run ? <Th className="w-1" /> : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => {
                const on = selected.has(c.name);
                const title = titles.get(c.name);
                const note = notes.data?.[c.name];
                return (
                  <tr
                    key={c.id}
                    onClick={() => toggle(c.name)}
                    className={cn('cursor-pointer', on ? 'bg-accent-soft' : 'hover:bg-canvas')}
                  >
                    <Td>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(c.name)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={t('automation.project.classes.select', { name: c.name })}
                        className="size-4 rounded border-border"
                      />
                    </Td>
                    <Td>
                      {title ? (
                        <span className="flex flex-col gap-0.5">
                          <span className="font-medium" title={title.full}>
                            {title.short}
                          </span>
                          <span className="font-mono text-[11.5px] text-ink-subtle">{c.name}</span>
                        </span>
                      ) : (
                        <span className="font-mono font-semibold">{c.name}</span>
                      )}
                      <CaseKeys
                        refs={(caseRefs.data ?? []).filter((ref) => ref.className === c.name)}
                      />
                      {note ? (
                        <span className="mt-1 flex max-w-sm items-start gap-1 text-[12px] text-block">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                          <span className="whitespace-pre-wrap">{note.text}</span>
                        </span>
                      ) : null}
                    </Td>
                    <Td className="text-ink-muted">{c.suite}</Td>
                    <Td className="text-ink-subtle">{c.testCount}</Td>
                    <Td>
                      <span className="flex flex-wrap gap-1">
                        {c.tags.map((tag) => (
                          <Badge key={tag}>{tag}</Badge>
                        ))}
                      </span>
                    </Td>
                    {can.run ? (
                      <Td>
                        <span
                          className="flex justify-end gap-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            aria-label={
                              note
                                ? t('automation.project.classes.editNote', { name: c.name })
                                : t('automation.project.classes.addNote', { name: c.name })
                            }
                            title={
                              note
                                ? t('automation.project.classes.editNote', { name: c.name })
                                : t('automation.project.classes.addNote', { name: c.name })
                            }
                            onClick={() => setNoteFor(c.name)}
                          >
                            <MessageSquarePlus className="size-3.5" />
                          </Button>
                          <Button size="sm" onClick={() => startRun([c.name])}>
                            <Play className="size-3.5" />
                            {t('automation.project.classes.run')}
                          </Button>
                        </span>
                      </Td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      {noteFor ? (
        <ClassNoteDialog
          projectId={projectId}
          className={noteFor}
          initial={notes.data?.[noteFor]?.text ?? ''}
          onClose={() => setNoteFor(null)}
        />
      ) : null}
    </div>
  );
};
